import { useState, useEffect, useCallback } from 'react';
import {
  Stack,
  Text,
  Switch,
  Select,
  MultiSelect,
  TextInput,
  NumberInput,
  Loader,
  Alert,
} from '@mantine/core';

import {
  usePartnerRoleAttributeRepository,
  usePartnerAttributeValueRepository,
} from '@ayocore/erp/crm/client/rpc';

interface AttrOption {
  id: number;
  value: string;
  label: string;
  sort_order: number;
  is_default: boolean;
  is_active: boolean;
}

interface AttrDefinition {
  id: number;
  code: string;
  label: string;
  value_type: 'string' | 'number' | 'boolean' | 'enum';
  is_multiple: boolean;
  is_required: boolean;
  default_value: string | null;
  options: AttrOption[];
}

interface AttrValue {
  attribute_id: number;
  value: string | null;
  option_id: number | null;
  option_value?: string;
}

interface PartnerAttributesPanelProps {
  partnerId: number;
}

/**
 * Компонент атрибутов партнёра (EAV).
 *
 * Загружает роли партнёра, определения атрибутов и текущие значения,
 * рендерит плоским списком, сохраняет при изменении.
 */
export function PartnerAttributesPanel({ partnerId }: PartnerAttributesPanelProps) {
  const attrRepo = usePartnerRoleAttributeRepository();
  const valueRepo = usePartnerAttributeValueRepository();

  const [attributes, setAttributes] = useState<AttrDefinition[]>([]);
  const [values, setValues] = useState<AttrValue[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [attrs, vals] = await Promise.all([
        attrRepo.getAttributesForPartner(partnerId),
        valueRepo.getByPartner(partnerId),
      ]);

      setAttributes(attrs as AttrDefinition[]);
      setValues(vals as AttrValue[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось загрузить атрибуты');
    } finally {
      setLoading(false);
    }
  }, [partnerId, attrRepo, valueRepo]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const getAttrValue = useCallback(
    (attrId: number) => {
      return values.filter(v => v.attribute_id === attrId);
    },
    [values]
  );

  const handleSetValue = useCallback(
    async (attrId: number, value: string | null, optionId?: number | null) => {
      try {
        await valueRepo.setValue(partnerId, attrId, value, optionId);
        setValues(prev => {
          const filtered = prev.filter(v => v.attribute_id !== attrId);
          if (value !== null || optionId) {
            filtered.push({ attribute_id: attrId, value, option_id: optionId ?? null });
          }
          return filtered;
        });
      } catch {
        // silent
      }
    },
    [partnerId, valueRepo]
  );

  const handleSetMultiValues = useCallback(
    async (attrId: number, optionIds: number[]) => {
      try {
        await valueRepo.setMultiValues(partnerId, attrId, optionIds);
        setValues(prev => {
          const filtered = prev.filter(v => v.attribute_id !== attrId);
          for (const optId of optionIds) {
            filtered.push({ attribute_id: attrId, value: null, option_id: optId });
          }
          return filtered;
        });
      } catch {
        // silent
      }
    },
    [partnerId, valueRepo]
  );

  if (loading) return <Loader size='xs' />;
  if (error) return <Alert color='red'>{error}</Alert>;
  if (attributes.length === 0)
    return (
      <Text size='xs' c='dimmed'>
        Нет дополнительных атрибутов для ролей этого партнёра
      </Text>
    );

  return (
    <Stack gap='xs'>
      {attributes.map(attr => (
        <AttributeField
          key={attr.id}
          attribute={attr}
          values={getAttrValue(attr.id)}
          onSetValue={handleSetValue}
          onSetMultiValues={handleSetMultiValues}
        />
      ))}
    </Stack>
  );
}

interface AttributeFieldProps {
  attribute: AttrDefinition;
  values: AttrValue[];
  onSetValue: (attrId: number, value: string | null, optionId?: number | null) => Promise<void>;
  onSetMultiValues: (attrId: number, optionIds: number[]) => Promise<void>;
}

function AttributeField({ attribute, values, onSetValue, onSetMultiValues }: AttributeFieldProps) {
  const { id, label, value_type, is_multiple, options, default_value } = attribute;
  const activeOptions = options.filter(o => o.is_active);

  switch (value_type) {
    case 'boolean': {
      const currentVal = values[0]?.value;
      const checked = currentVal !== undefined ? currentVal === 'true' : default_value === 'true';
      return (
        <Switch
          label={label}
          checked={checked}
          onChange={e => void onSetValue(id, String(e.currentTarget.checked))}
          size='sm'
        />
      );
    }

    case 'enum': {
      if (is_multiple) {
        const selectedIds = values
          .map(v => v.option_id)
          .filter((optId): optId is number => optId !== null)
          .map(String);

        return (
          <MultiSelect
            label={label}
            size='xs'
            data={activeOptions.map(o => ({ value: String(o.id), label: o.label }))}
            value={selectedIds}
            onChange={selected => void onSetMultiValues(id, selected.map(Number))}
          />
        );
      }

      const selectedOptionId = values[0]?.option_id;
      const defaultOption = activeOptions.find(o => o.is_default);
      const currentValue = selectedOptionId
        ? String(selectedOptionId)
        : defaultOption
          ? String(defaultOption.id)
          : null;

      return (
        <Select
          label={label}
          size='xs'
          data={activeOptions.map(o => ({ value: String(o.id), label: o.label }))}
          value={currentValue}
          onChange={val => {
            if (val) {
              const opt = activeOptions.find(o => String(o.id) === val);
              void onSetValue(id, opt?.value ?? null, Number(val));
            } else {
              void onSetValue(id, null, null);
            }
          }}
          clearable
        />
      );
    }

    case 'number': {
      const currentVal = values[0]?.value;
      const numValue =
        currentVal !== undefined
          ? Number(currentVal)
          : default_value
            ? Number(default_value)
            : undefined;

      return (
        <NumberInput
          label={label}
          size='xs'
          value={numValue}
          onChange={val => void onSetValue(id, val !== '' ? String(val) : null)}
        />
      );
    }

    case 'string':
    default: {
      const currentVal = values[0]?.value ?? default_value ?? '';

      return (
        <TextInput
          label={label}
          size='xs'
          value={currentVal}
          onBlur={e => void onSetValue(id, e.currentTarget.value || null)}
        />
      );
    }
  }
}
